import { inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, lastValueFrom, map, retry, throwError, timer } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiCache } from './api-cache';

const CACHE_TTL = {
  REALM: 15 * 60 * 1000, // 15 min — realm config rarely changes
  CLIENTS: 10 * 60 * 1000, // 10 min — client list is stable within a session
  CLIENT_ROLES: 10 * 60 * 1000, // 10 min — roles rarely change
  LOCALES: 30 * 60 * 1000, // 30 min — locale list almost never changes
} as const;

export interface RealmRepresentation {
  id?: string;
  realm?: string;
  displayName?: string;
  attributes?: {
    // Azure Graph Group Sync
    'azure.group-sync.enabled': string;
    'azure.group-sync.client-id': string;
    'azure.group-sync.client-secret': string;
    'azure.group-sync.tenant-id': string;
    'azure.group-sync.parent-group': string;

    // Additional attributes
    [key: string]: string;
  };
}

export interface ProtocolMapperRepresentation {
  id?: string;
  name?: string;
  protocol?: string;
  protocolMapper?: string;
  consentRequired?: boolean;
  config?: Record<string, string>;
}

export interface ResourceServerRepresentation {
  allowRemoteResourceManagement?: boolean;
  clientId?: string;
  id?: string;
  name?: string;
  policies?: Record<string, unknown>[];
  policyEnforcementMode?: string;
  resources?: Record<string, unknown>[];
  scopes?: Record<string, unknown>[];
  decisionStrategy?: string;
}

export interface ClientRepresentation {
  id?: string;
  clientId?: string;
  name?: string;
  description?: string;
  type?: string;
  rootUrl?: string;
  adminUrl?: string;
  baseUrl?: string;
  surrogateAuthRequired?: boolean;
  enabled?: boolean;
  alwaysDisplayInConsole?: boolean;
  clientAuthenticatorType?: string;
  secret?: string;
  registrationAccessToken?: string;
  defaultRoles?: string[];
  redirectUris?: string[];
  webOrigins?: string[];
  notBefore?: number;
  bearerOnly?: boolean;
  consentRequired?: boolean;
  standardFlowEnabled?: boolean;
  implicitFlowEnabled?: boolean;
  directAccessGrantsEnabled?: boolean;
  serviceAccountsEnabled?: boolean;
  authorizationServicesEnabled?: boolean;
  directGrantsOnly?: boolean;
  publicClient?: boolean;
  frontchannelLogout?: boolean;
  protocol?: string;
  attributes?: {
    // Salesforce customer sync
    'salesforce.campaign-id'?: string;
    hideSocialProviders?: string;
    // Logout URLs. If multiple URLs, they are separated by '##'
    'post.logout.redirect.uris'?: string;

    // Custom user access control
    // User type requirement: 'employee'|'customer'|''
    'access.requires.userType'?: 'employee' | 'customer' | '' | undefined;
    // Require any client role: 'true'|'false'|''
    'access.requires.anyClientRole'?: 'true' | 'false' | '' | undefined;
    // Required roles (JSON array of role names)
    'access.requires.roles'?: string;
    // Role matching mode: 'ANY'|'ALL'|'DENY'|''
    'access.requires.roles.mode'?: 'ANY' | 'ALL' | 'DENY' | '' | undefined;
    // Required countries (JSON array of country codes)
    'access.requires.countries'?: string;
    // Required attributes (JSON object with attribute name to regex pattern mapping)
    'access.requires.attributes'?: string;
    // Access denial message key for i18n
    'access.deny.messageKey'?: string;
    // Redirect URI when access is denied
    'access.deny.redirectUri'?: string;

    // Additional attributes
    [key: string]: string | undefined;
  };
  authenticationFlowBindingOverrides?: Record<string, string>;
  fullScopeAllowed?: boolean;
  nodeReRegistrationTimeout?: number;
  registeredNodes?: Record<string, number>;
  protocolMappers?: ProtocolMapperRepresentation[];
  clientTemplate?: string;
  useTemplateConfig?: boolean;
  useTemplateScope?: boolean;
  useTemplateMappers?: boolean;
  defaultClientScopes?: string[];
  optionalClientScopes?: string[];
  authorizationSettings?: ResourceServerRepresentation;
  access?: {
    view?: boolean;
    manage?: boolean;
    'map-roles-composite': boolean;
    'map-roles': boolean;
    'map-roles-client-scope': boolean;
  };
  origin?: string;
}

export interface RoleRepresentation {
  id?: string;
  name?: string;
  description?: string;
  composite?: boolean;
  clientRole?: boolean;
  containerId?: string;
  attributes?: Record<string, string[]>;
  // Response-only: resolved owning client name, or 'Realm' for realm-level roles
  clientLabel?: string | null;
}

// One source (a client, or 'REALM') and the roles it granted to a service account
export interface ServiceAccountRoleGroup {
  roleClientId: string; // clientId of the granting client, or the literal 'REALM'
  clientLabel: string;
  roles: RoleRepresentation[];
}

// User management interfaces
export interface UserRepresentation {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  emailVerified?: boolean;
  federationLink?: string;
  attributes?: Record<string, string[]>;
  requiredActions?: string[];
  createdTimestamp?: number;
  groups?: string[];
  federatedIdentities?: Record<string, unknown>[];
  access?: {
    'map-roles': boolean;
    view?: boolean;
    manage?: boolean;
    impersonate: boolean;
    'manage-group-membership'?: boolean;
  };
}

// Field filters use Keycloak's contains-match semantics unless `exact` is set
export interface UserSearchFilter {
  search?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  exact?: boolean;
}

export interface CredentialRepresentation {
  type?: string;
  value?: string;
  temporary?: boolean;
}

export interface GroupRepresentation {
  id?: string;
  name?: string;
  path?: string;
  subGroups?: GroupRepresentation[];
  subGroupCount?: number;
  attributes?: Record<string, string[]>;
  access?: {
    view?: boolean;
    manage?: boolean;
    manageMembership?: boolean;
    viewMembers?: boolean;
  };
}

export interface RoleMappingRepresentation {
  realmMappings?: RoleRepresentation[];
  clientMappings?: Record<string, ClientRoleMappingRepresentation>;
}

export interface ClientRoleMappingRepresentation {
  id?: string;
  client?: string;
  mappings?: RoleRepresentation[];
}

export interface ClientScopeRepresentation {
  id?: string;
  name?: string;
  description?: string;
  protocol?: string;
  attributes?: Record<string, string>;
  protocolMappers?: ProtocolMapperRepresentation[];
}

export type ClientRequestStatus = 'PENDING' | 'APPROVING' | 'APPROVED' | 'DENIED';

export interface ClientRequest {
  id: string;
  status: ClientRequestStatus;
  requestedByUserId: string;
  requestedByEmail: string;
  requestedByName: string;
  requestedName: string;
  requestedClientId: string;
  createdAt: string;
  reviewedByUserId?: string;
  reviewedByEmail?: string;
  reviewedAt?: string;
  finalClientId?: string;
  denyReason?: string;
}

function clientRequestError(error: unknown, fallback: string): Error {
  const body = (error as { error?: { error?: unknown } } | null)?.error;
  return new Error(typeof body?.error === 'string' ? body.error : fallback);
}

@Injectable({
  providedIn: 'root',
})
export class KeycloakAdminApi {
  private http = inject(HttpClient);
  private oauthService = inject(OAuthService);

  // private baseUrl: string = 'https://auth.dev.ebmpapst.net/admin/realms/ebm-papst';
  private baseUrl: string = environment.api.baseUrl;
  private cache = new ApiCache();

  // Helper method to get headers with authorization
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.oauthService.getAccessToken()}`,
    });
  }

  // ME - info on permissions
  getMe(): Promise<{
    isRealmAdmin: boolean;
    accessibleClients: {
      id?: string;
      clientId?: string;
      canManage: boolean;
      permissions?: string[];
    }[];
  }> {
    return lastValueFrom(
      this.http
        .get<any>(`${environment.api.lambdaBaseUrl}/me`, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error fetching me: ${error.message}`)))
    );
  }

  // REALM METHODS

  /**
   * Get realm details including attributes
   */
  getRealmDetails(): Promise<RealmRepresentation> {
    return this.cache.getOrFetch('realm:details', CACHE_TTL.REALM, () =>
      lastValueFrom(
        this.http
          .get<RealmRepresentation>(`${this.baseUrl}`, { headers: this.getHeaders() })
          .pipe(
            catchError((error) =>
              throwError(() => `Error fetching realm details: ${error.message}`)
            )
          )
      )
    );
  }

  /**
   * Update realm with new attributes
   */
  updateRealmAttributes(realmData: RealmRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(`${this.baseUrl}`, realmData, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error updating realm: ${error.message}`)))
    ).then(() => {
      this.cache.invalidate('realm:details');
    });
  }

  // CLIENT REQUEST METHODS

  createClientRequest(name: string, clientId: string): Promise<ClientRequest> {
    return lastValueFrom(
      this.http
        .post<ClientRequest>(
          `${environment.api.lambdaBaseUrl}/client-requests`,
          { name, clientId },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => clientRequestError(error, 'Failed to submit client request'))
          )
        )
    );
  }

  getMyClientRequests(): Promise<ClientRequest[]> {
    return lastValueFrom(
      this.http
        .get<ClientRequest[]>(`${environment.api.lambdaBaseUrl}/client-requests`, {
          headers: this.getHeaders(),
          params: new HttpParams().set('mine', 'true'),
        })
        .pipe(
          catchError((error) =>
            throwError(() => clientRequestError(error, 'Failed to load your client requests'))
          )
        )
    );
  }

  getAllClientRequests(): Promise<ClientRequest[]> {
    return lastValueFrom(
      this.http
        .get<ClientRequest[]>(`${environment.api.lambdaBaseUrl}/client-requests`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) =>
            throwError(() => clientRequestError(error, 'Failed to load client requests'))
          )
        )
    );
  }

  approveClientRequest(
    id: string,
    overrides: { clientId?: string; name?: string } = {}
  ): Promise<{ finalClientId: string }> {
    return lastValueFrom(
      this.http
        .post<{ finalClientId: string }>(
          `${environment.api.lambdaBaseUrl}/client-requests/${id}/approve`,
          overrides,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => clientRequestError(error, 'Failed to approve client request'))
          )
        )
    ).then((result) => {
      this.cache.invalidateByPrefix('clients:');
      return result;
    });
  }

  denyClientRequest(id: string, reason: string): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(
          `${environment.api.lambdaBaseUrl}/client-requests/${id}/deny`,
          { reason },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => clientRequestError(error, 'Failed to deny client request'))
          )
        )
    );
  }

  // CLIENT METHODS

  // NOTE: There is no /clients/count endpoint in Keycloak, so total client count cannot be fetched directly.
  // For pagination, use the length of the returned array and infer "has more" if length === page size.

  /**
   * Create a new client
   */
  createClient(clientData: ClientRepresentation): Promise<ClientRepresentation> {
    return lastValueFrom(
      this.http
        .post<ClientRepresentation>(`${this.baseUrl}/clients`, clientData, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error creating client: ${error.message}`)))
    ).then((result) => {
      this.cache.invalidateByPrefix('clients:');
      return result;
    });
  }

  // CLIENT METHODS (via Lambda — uses service credentials, enforces permission filtering)

  getClients(search?: string, first?: number, max?: number): Promise<ClientRepresentation[]> {
    const cacheKey = `clients:list:${search ?? ''}:${first ?? ''}:${max ?? ''}`;
    return this.cache.getOrFetch(cacheKey, CACHE_TTL.CLIENTS, () => {
      let params = new HttpParams();
      if (search) params = params.set('search', search);
      if (first !== undefined) params = params.set('first', first.toString());
      if (max !== undefined) params = params.set('max', max.toString());
      return lastValueFrom(
        this.http
          .get<ClientRepresentation[]>(`${environment.api.lambdaBaseUrl}/clients`, {
            headers: this.getHeaders(),
            params,
          })
          .pipe(catchError((error) => throwError(() => `Error fetching clients: ${error.message}`)))
      );
    });
  }

  getClientsPaginated(
    search?: string,
    first?: number,
    max?: number
  ): Promise<ClientRepresentation[]> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
      params = params.set('searchType', 'clientId');
    }
    if (first !== undefined) params = params.set('first', first.toString());
    if (max !== undefined) params = params.set('max', max.toString());
    return lastValueFrom(
      this.http
        .get<ClientRepresentation[]>(`${environment.api.lambdaBaseUrl}/clients`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(catchError((error) => throwError(() => `Error fetching clients: ${error.message}`)))
    );
  }

  getClientDetails(clientId: string): Promise<ClientRepresentation> {
    return this.getClientById(clientId); // same method
  }

  getClientById(clientId: string): Promise<ClientRepresentation> {
    if (!clientId) {
      return Promise.reject('Client ID is required');
    }
    return lastValueFrom(
      this.http
        .get<ClientRepresentation>(`${environment.api.lambdaBaseUrl}/clients/${clientId}`, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error fetching client: ${error.message}`)))
    );
  }

  updateClientAttributes(clientId: string, clientData: ClientRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put(`${environment.api.lambdaBaseUrl}/clients/${clientId}`, clientData, {
          headers: this.getHeaders(),
          responseType: 'text',
        })
        .pipe(
          retry({
            count: 1,
            delay: (error, retryCount) => {
              const status = error?.status;
              if (status === 502 || status === 503 || status === 504) {
                return timer(retryCount * 1000);
              }
              throw error;
            },
          }),
          catchError((error) => {
            const status = error?.status ? ` (HTTP ${error.status})` : '';
            return throwError(
              () => `Error updating client${status}: ${error.statusText || error.message || error}`
            );
          })
        )
    ).then(() => {
      this.cache.invalidateByPrefix('clients:');
    });
  }

  // CLIENT ROLES METHODS

  getClientRoles(clientId: string): Promise<RoleRepresentation[]> {
    return this.cache.getOrFetch(`clientRoles:${clientId}`, CACHE_TTL.CLIENT_ROLES, () =>
      lastValueFrom(
        this.http
          .get<RoleRepresentation[]>(`${environment.api.lambdaBaseUrl}/clients/${clientId}/roles`, {
            headers: this.getHeaders(),
          })
          .pipe(
            catchError((error) => throwError(() => `Error fetching client roles: ${error.message}`))
          )
      )
    );
  }

  /**
   * Get a specific client role by name
   */
  getClientRole(clientId: string, roleName: string): Promise<RoleRepresentation> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation>(`${this.baseUrl}/clients/${clientId}/roles/${roleName}`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching client role: ${error.message}`))
        )
    );
  }

  /**
   * Create a new client role
   */
  createClientRole(clientId: string, role: RoleRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(`${environment.api.lambdaBaseUrl}/clients/${clientId}/roles`, role, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error creating client role: ${error.message}`))
        )
    ).then(() => {
      this.cache.invalidate(`clientRoles:${clientId}`);
    });
  }

  /**
   * Update an existing client role
   */
  updateClientRole(clientId: string, roleName: string, role: RoleRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(`${environment.api.lambdaBaseUrl}/clients/${clientId}/roles/${roleName}`, role, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error updating client role: ${error.message}`))
        )
    ).then(() => {
      this.cache.invalidate(`clientRoles:${clientId}`);
    });
  }

  /**
   * Delete a client role
   */
  deleteClientRole(clientId: string, roleName: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(`${environment.api.lambdaBaseUrl}/clients/${clientId}/roles/${roleName}`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error deleting client role: ${error.message}`))
        )
    ).then(() => {
      this.cache.invalidate(`clientRoles:${clientId}`);
    });
  }

  /**
   * Get the member roles of a composite client role
   */
  getClientRoleComposites(clientId: string, roleName: string): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/roles/${roleName}/composites`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching role composites: ${error.message}`)
          )
        )
    );
  }

  /**
   * Add member roles to a composite client role
   */
  addClientRoleComposites(
    clientId: string,
    roleName: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/roles/${roleName}/composites`,
          roles,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) => throwError(() => `Error adding role composites: ${error.message}`))
        )
    ).then(() => {
      this.cache.invalidate(`clientRoles:${clientId}`);
    });
  }

  /**
   * Remove member roles from a composite client role
   */
  removeClientRoleComposites(
    clientId: string,
    roleName: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .request<void>(
          'DELETE',
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/roles/${roleName}/composites`,
          { headers: this.getHeaders(), body: roles }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error removing role composites: ${error.message}`)
          )
        )
    ).then(() => {
      this.cache.invalidate(`clientRoles:${clientId}`);
    });
  }

  /**
   * Search realm-level roles (realm admin only)
   */
  getRealmRoles(search?: string): Promise<RoleRepresentation[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(`${environment.api.lambdaBaseUrl}/realm-roles`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching realm roles: ${error.message}`))
        )
    );
  }

  /**
   * Builds the base path for a client's service-account role mappings against a given role
   * scope: either another client's id, or the literal 'REALM' for realm-level roles.
   */
  private serviceAccountRoleMappingsPath(clientId: string, roleClientId: string): string {
    const scopeSegment =
      roleClientId === 'REALM' ? 'realm' : `clients/${encodeURIComponent(roleClientId)}`;
    return `${environment.api.lambdaBaseUrl}/clients/${clientId}/service-account/roles/${scopeSegment}`;
  }

  getServiceAccountRoleMappings(
    clientId: string,
    roleClientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(this.serviceAccountRoleMappingsPath(clientId, roleClientId), {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching service account roles: ${error.message}`)
          )
        )
    );
  }

  getEffectiveServiceAccountRoleMappings(
    clientId: string,
    roleClientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${this.serviceAccountRoleMappingsPath(clientId, roleClientId)}/composite`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching effective service account roles: ${error.message}`)
          )
        )
    );
  }

  getAvailableServiceAccountRoleMappings(
    clientId: string,
    roleClientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${this.serviceAccountRoleMappingsPath(clientId, roleClientId)}/available`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching available service account roles: ${error.message}`)
          )
        )
    );
  }

  /**
   * Fetches every role assigned to the client's service account, grouped by source
   * (each other client that granted a role, plus realm roles), regardless of scope.
   */
  getAllServiceAccountRoleMappings(clientId: string): Promise<ServiceAccountRoleGroup[]> {
    return lastValueFrom(
      this.http
        .get<ServiceAccountRoleGroup[]>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/service-account/roles`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching all service account roles: ${error.message}`)
          )
        )
    );
  }

  /**
   * Assign a role (from roleClientId, or 'REALM') to a client's service account
   */
  assignServiceAccountRole(
    clientId: string,
    roleClientId: string,
    role: RoleRepresentation
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .post<{ success: boolean; message: string }>(
          `${environment.api.lambdaBaseUrl}/roles/service-accounts`,
          { clientId, roleClientId, roleName: role.name, action: 'assign' },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error assigning role ${role.name}: ${error.message}`)
          )
        )
    ).then(() => void 0);
  }

  /**
   * Remove a role (from roleClientId, or 'REALM') from a client's service account
   */
  removeServiceAccountRole(
    clientId: string,
    roleClientId: string,
    role: RoleRepresentation
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .post<{ success: boolean; message: string }>(
          `${environment.api.lambdaBaseUrl}/roles/service-accounts`,
          { clientId, roleClientId, roleName: role.name, action: 'unassign' },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error removing role ${role.name}: ${error.message}`)
          )
        )
    ).then(() => void 0);
  }

  // CLIENT PERMISSION METHODS

  getClientPermissions(clientId: string): Promise<{ user: string; permission: string }[]> {
    return lastValueFrom(
      this.http
        .get<{ user: string; permission: string }[]>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/permissions`,
          {
            headers: this.getHeaders(),
          }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching client permissions: ${error.message}`)
          )
        )
    );
  }

  saveClientPermissions(
    clientId: string,
    permissions: { user: string; permission: string }[]
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/permissions`,
          permissions,
          {
            headers: this.getHeaders(),
          }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error saving client permissions: ${error.message}`)
          )
        )
    );
  }

  // USER METHODS (via Lambda — uses service credentials)

  private buildUserFilterParams(filter?: UserSearchFilter): HttpParams {
    let params = new HttpParams();
    if (!filter) return params;
    if (filter.search) params = params.set('search', filter.search);
    if (filter.username) params = params.set('username', filter.username);
    if (filter.email) params = params.set('email', filter.email);
    if (filter.firstName) params = params.set('firstName', filter.firstName);
    if (filter.lastName) params = params.set('lastName', filter.lastName);
    if (filter.exact !== undefined) params = params.set('exact', filter.exact.toString());
    return params;
  }

  getUsers(filter?: UserSearchFilter, first?: number, max?: number): Promise<UserRepresentation[]> {
    let params = this.buildUserFilterParams(filter);
    if (first !== undefined) params = params.set('first', first.toString());
    if (max !== undefined) params = params.set('max', max.toString());
    return lastValueFrom(
      this.http
        .get<UserRepresentation[]>(`${environment.api.lambdaBaseUrl}/users`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(catchError((error) => throwError(() => `Error fetching users: ${error.message}`)))
    );
  }

  getUsersCount(filter?: UserSearchFilter): Promise<number> {
    const params = this.buildUserFilterParams(filter);
    return lastValueFrom(
      this.http
        .get<{ count: number }>(`${environment.api.lambdaBaseUrl}/users/count`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          map((response) => response.count),
          catchError((error) => throwError(() => `Error fetching users count: ${error.message}`))
        )
    );
  }

  getUserById(userId: string): Promise<UserRepresentation> {
    return lastValueFrom(
      this.http
        .get<UserRepresentation>(`${environment.api.lambdaBaseUrl}/users/${userId}`, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error fetching user: ${error.message}`)))
    );
  }

  /**
   * Create a new user
   */
  createUser(user: UserRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(`${this.baseUrl}/users`, user, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error creating user: ${error.message}`)))
    );
  }

  /**
   * Update existing user
   */
  updateUser(userId: string, user: UserRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(`${this.baseUrl}/users/${userId}`, user, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error updating user: ${error.message}`)))
    );
  }

  /**
   * Delete a user
   */
  deleteUser(userId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(`${this.baseUrl}/users/${userId}`, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error deleting user: ${error.message}`)))
    );
  }

  /**
   * Reset user password
   */
  resetPassword(userId: string, credential: CredentialRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(`${this.baseUrl}/users/${userId}/reset-password`, credential, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error resetting password: ${error.message}`)))
    );
  }

  /**
   * Invalidate Salesforce customer info cache by clearing the last-check-customerno attribute
   */
  invalidateSalesforceCache(userId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .post<{
          success: boolean;
          message: string;
        }>(
          `${environment.api.lambdaBaseUrl}/users/invalidate-salesforce-cache`,
          { userId },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error invalidating Salesforce cache: ${error.message}`)
          )
        )
    ).then(() => void 0);
  }

  /**
   * Search users for group management autocomplete
   */
  searchUsersForGroupManagement(search: string): Promise<UserRepresentation[]> {
    return this.getUsers({ search }, 0, 20);
  }

  getUserGroups(userId: string): Promise<GroupRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<GroupRepresentation[]>(`${environment.api.lambdaBaseUrl}/users/${userId}/groups`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching user groups: ${error.message}`))
        )
    );
  }

  // GROUP METHODS (via Lambda — uses service credentials)

  getGroups(search?: string, first?: number, max?: number): Promise<GroupRepresentation[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (first !== undefined) params = params.set('first', first.toString());
    if (max !== undefined) params = params.set('max', max.toString());
    return lastValueFrom(
      this.http
        .get<GroupRepresentation[]>(`${environment.api.lambdaBaseUrl}/groups`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(catchError((error) => throwError(() => `Error fetching groups: ${error.message}`)))
    );
  }

  getGroupsCount(search?: string, top?: boolean): Promise<{ count: number }> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (top !== undefined) params = params.set('top', String(top));
    return lastValueFrom(
      this.http
        .get<{ count: number }>(`${environment.api.lambdaBaseUrl}/groups/count`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching groups count: ${error.message}`))
        )
    );
  }

  /**
   * Get all groups in the realm (handles pagination automatically)
   */
  getAllGroups(search?: string): Promise<GroupRepresentation[]> {
    const batchSize = 100;

    const fetchBatch = async (first: number): Promise<GroupRepresentation[]> => {
      const batch = await this.getGroups(search, first, batchSize);

      if (batch.length < batchSize) {
        // Last batch
        return batch;
      } else {
        // More batches to fetch
        const nextBatch = await fetchBatch(first + batchSize);
        return [...batch, ...nextBatch];
      }
    };

    return fetchBatch(0);
  }

  /**
   * Get all groups recursively, loading subgroups for complete hierarchy
   */
  getAllGroupsRecursive(): Promise<GroupRepresentation[]> {
    const loadSubgroups = async (group: GroupRepresentation): Promise<void> => {
      if (
        group.subGroupCount &&
        group.subGroupCount > 0 &&
        (!group.subGroups || group.subGroups.length === 0)
      ) {
        try {
          group.subGroups = await this.getGroupChildren(group.id!);
          // Recursively load subgroups of subgroups
          for (const subGroup of group.subGroups) {
            await loadSubgroups(subGroup);
          }
        } catch (error) {
          console.warn(`Failed to load subgroups for ${group.name}:`, error);
          group.subGroups = [];
        }
      } else if (group.subGroups && group.subGroups.length > 0) {
        // Recursively load subgroups of already loaded subgroups
        for (const subGroup of group.subGroups) {
          await loadSubgroups(subGroup);
        }
      }
    };

    return this.getAllGroups().then(async (topLevelGroups) => {
      // Load all subgroups recursively
      for (const group of topLevelGroups) {
        await loadSubgroups(group);
      }
      return topLevelGroups;
    });
  }

  /**
   * Get count of subgroups for a specific group
   */
  getGroupChildrenCount(groupId: string, search?: string): Promise<number> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);

    return lastValueFrom(
      this.http
        .get<number>(`${environment.api.lambdaBaseUrl}/groups/${groupId}/children/count`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching group children count: ${error.message}`)
          )
        )
    );
  }

  /**
   * Add user to a group
   */
  addUserToGroup(userId: string, groupId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(
          `${this.baseUrl}/users/${userId}/groups/${groupId}`,
          {},
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) => throwError(() => `Error adding user to group: ${error.message}`))
        )
    );
  }

  /**
   * Remove user from a group
   */
  removeUserFromGroup(userId: string, groupId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(`${this.baseUrl}/users/${userId}/groups/${groupId}`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) =>
            throwError(() => `Error removing user from group: ${error.message}`)
          )
        )
    );
  }

  /**
   * Get role mappings for a user (outside client roles)
   */
  getUserRoleMappings(userId: string): Promise<RoleMappingRepresentation> {
    return lastValueFrom(
      this.http
        .get<RoleMappingRepresentation>(`${this.baseUrl}/users/${userId}/role-mappings`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching user role mappings: ${error.message}`)
          )
        )
    );
  }

  getUserClientRoleMappings(userId: string, clientId: string): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/users/${userId}/role-mappings/clients/${clientId}`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching user client roles: ${error.message}`)
          )
        )
    );
  }

  getEffectiveUserClientRoleMappings(
    userId: string,
    clientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/users/${userId}/role-mappings/clients/${clientId}/composite`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching effective client roles: ${error.message}`)
          )
        )
    );
  }

  getAvailableUserClientRoleMappings(
    userId: string,
    clientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/users/${userId}/role-mappings/clients/${clientId}/available`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching available client roles: ${error.message}`)
          )
        )
    );
  }

  /**
   * Assign client roles to a user via Lambda API
   */
  addClientRolesToUser(
    userId: string,
    clientId: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    const requests = roles.map((role) =>
      lastValueFrom(
        this.http
          .post<{ success: boolean; message: string }>(
            `${environment.api.lambdaBaseUrl}/roles/users`,
            {
              userId,
              clientId,
              roleName: role.name,
              action: 'assign',
            },
            { headers: this.getHeaders() }
          )
          .pipe(
            catchError((error) =>
              throwError(() => `Error assigning role ${role.name}: ${error.message}`)
            )
          )
      )
    );

    return Promise.all(requests).then(() => void 0);
  }

  /**
   * Remove client roles from a user via Lambda API
   */
  removeClientRolesFromUser(
    userId: string,
    clientId: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    const requests = roles.map((role) =>
      lastValueFrom(
        this.http
          .post<{ success: boolean; message: string }>(
            `${environment.api.lambdaBaseUrl}/roles/users`,
            {
              userId,
              clientId,
              roleName: role.name,
              action: 'unassign',
            },
            { headers: this.getHeaders() }
          )
          .pipe(
            catchError((error) =>
              throwError(() => `Error removing role ${role.name}: ${error.message}`)
            )
          )
      )
    );

    return Promise.all(requests).then(() => void 0);
  }

  /**
   * Get direct child groups of a specific group
   */
  getGroupChildren(
    groupId: string,
    search?: string,
    first?: number,
    max?: number
  ): Promise<GroupRepresentation[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (first !== undefined) params = params.set('first', first.toString());
    if (max !== undefined) params = params.set('max', max.toString());

    return lastValueFrom(
      this.http
        .get<GroupRepresentation[]>(`${environment.api.lambdaBaseUrl}/groups/${groupId}/children`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching group children: ${error.message}`))
        )
    );
  }

  /**
   * Get group by ID
   */
  getGroupById(groupId: string): Promise<GroupRepresentation> {
    return lastValueFrom(
      this.http
        .get<GroupRepresentation>(`${environment.api.lambdaBaseUrl}/groups/${groupId}`, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error fetching group: ${error.message}`)))
    );
  }

  /**
   * Create a new group
   */
  createGroup(group: GroupRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(`${this.baseUrl}/groups`, group, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error creating group: ${error.message}`)))
    );
  }

  /**
   * Create a new group as a subgroup of a parent group
   */
  createSubGroup(parentGroupId: string, group: GroupRepresentation): Promise<GroupRepresentation> {
    return lastValueFrom(
      this.http
        .post<GroupRepresentation>(`${this.baseUrl}/groups/${parentGroupId}/children`, group, {
          headers: this.getHeaders(),
        })
        .pipe(catchError((error) => throwError(() => `Error creating subgroup: ${error.message}`)))
    );
  }

  /**
   * Update existing group
   */
  updateGroup(groupId: string, group: GroupRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(`${this.baseUrl}/groups/${groupId}`, group, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error updating group: ${error.message}`)))
    );
  }

  /**
   * Delete a group
   */
  deleteGroup(groupId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(`${this.baseUrl}/groups/${groupId}`, { headers: this.getHeaders() })
        .pipe(catchError((error) => throwError(() => `Error deleting group: ${error.message}`)))
    );
  }

  getGroupMembers(groupId: string, first?: number, max?: number): Promise<UserRepresentation[]> {
    let params = new HttpParams();
    if (first !== undefined) params = params.set('first', first.toString());
    if (max !== undefined) params = params.set('max', max.toString());
    return lastValueFrom(
      this.http
        .get<UserRepresentation[]>(`${environment.api.lambdaBaseUrl}/groups/${groupId}/members`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching group members: ${error.message}`))
        )
    );
  }

  getGroupClientRoleMappings(groupId: string, clientId: string): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/groups/${groupId}/role-mappings/clients/${clientId}`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching group client roles: ${error.message}`)
          )
        )
    );
  }

  getEffectiveGroupClientRoleMappings(
    groupId: string,
    clientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/groups/${groupId}/role-mappings/clients/${clientId}/composite`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching effective group client roles: ${error.message}`)
          )
        )
    );
  }

  getAvailableGroupClientRoleMappings(
    groupId: string,
    clientId: string
  ): Promise<RoleRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<RoleRepresentation[]>(
          `${environment.api.lambdaBaseUrl}/groups/${groupId}/role-mappings/clients/${clientId}/available`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error fetching available group client roles: ${error.message}`)
          )
        )
    );
  }

  /**
   * Assign client roles to a group via Lambda API
   */
  addClientRolesToGroup(
    groupId: string,
    clientId: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    const requests = roles.map((role) =>
      lastValueFrom(
        this.http
          .post<{ success: boolean; message: string }>(
            `${environment.api.lambdaBaseUrl}/roles/groups`,
            {
              groupId,
              clientId,
              roleName: role.name,
              action: 'assign',
            },
            { headers: this.getHeaders() }
          )
          .pipe(
            catchError((error) =>
              throwError(() => `Error assigning role ${role.name} to group: ${error.message}`)
            )
          )
      )
    );

    return Promise.all(requests).then(() => void 0);
  }

  /**
   * Remove client roles from a group via Lambda API
   */
  removeClientRolesFromGroup(
    groupId: string,
    clientId: string,
    roles: RoleRepresentation[]
  ): Promise<void> {
    const requests = roles.map((role) =>
      lastValueFrom(
        this.http
          .post<{ success: boolean; message: string }>(
            `${environment.api.lambdaBaseUrl}/roles/groups`,
            {
              groupId,
              clientId,
              roleName: role.name,
              action: 'unassign',
            },
            { headers: this.getHeaders() }
          )
          .pipe(
            catchError((error) =>
              throwError(() => `Error removing role ${role.name} from group: ${error.message}`)
            )
          )
      )
    );

    return Promise.all(requests).then(() => void 0);
  }

  // CLIENT SCOPES METHODS

  /**
   * Get all client scopes in the realm
   */
  getClientScopes(): Promise<ClientScopeRepresentation[]> {
    return lastValueFrom(
      this.http
        .get<ClientScopeRepresentation[]>(`${this.baseUrl}/client-scopes`, {
          headers: this.getHeaders(),
        })
        .pipe(
          catchError((error) => throwError(() => `Error fetching client scopes: ${error.message}`))
        )
    );
  }

  /**
   * Get a client scope by name
   */
  async getClientScopeByName(scopeName: string): Promise<ClientScopeRepresentation | null> {
    try {
      const scopes = await this.getClientScopes();
      return scopes.find((scope) => scope.name === scopeName) || null;
    } catch (error) {
      throw new Error('Error finding client scope by name', { cause: error });
    }
  }

  /**
   * Add a default client scope to a client
   */
  addDefaultClientScope(clientId: string, clientScopeId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .put<void>(
          `${this.baseUrl}/clients/${clientId}/default-client-scopes/${clientScopeId}`,
          {},
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error adding default client scope: ${error.message}`)
          )
        )
    );
  }

  /**
   * Remove a default client scope from a client
   */
  removeDefaultClientScope(clientId: string, clientScopeId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(
          `${this.baseUrl}/clients/${clientId}/default-client-scopes/${clientScopeId}`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error removing default client scope: ${error.message}`)
          )
        )
    );
  }

  /**
   * Create a protocol mapper on a client (proxied through the lambda, which uses a service
   * account with sufficient Keycloak permissions — calling Keycloak's admin REST API directly
   * from the browser fails with 403 for users without full realm-admin rights)
   */
  createProtocolMapper(clientId: string, mapper: ProtocolMapperRepresentation): Promise<void> {
    return lastValueFrom(
      this.http
        .post<void>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/protocol-mappers`,
          mapper,
          {
            headers: this.getHeaders(),
          }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error creating protocol mapper: ${error.message}`)
          )
        )
    );
  }

  /**
   * Delete a protocol mapper from a client (proxied through the lambda, see createProtocolMapper)
   */
  deleteProtocolMapper(clientId: string, mapperId: string): Promise<void> {
    return lastValueFrom(
      this.http
        .delete<void>(
          `${environment.api.lambdaBaseUrl}/clients/${clientId}/protocol-mappers/${mapperId}`,
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error deleting protocol mapper: ${error.message}`)
          )
        )
    );
  }

  // LOCALIZATION METHODS

  /**
   * Get available locales configured in the Keycloak realm.
   * Results are cached via ApiCache (30 min TTL) to avoid repeated backend calls.
   */
  async getAvailableLocales(): Promise<string[]> {
    return this.cache.getOrFetch('locales', CACHE_TTL.LOCALES, async () => {
      const result = await lastValueFrom(
        this.http
          .get<{
            success: boolean;
            message: string;
            locales: string[];
          }>(`${environment.api.lambdaBaseUrl}/localization/locales`, {
            headers: this.getHeaders(),
          })
          .pipe(catchError((error) => throwError(() => `Error fetching locales: ${error.message}`)))
      );
      return result.locales || [];
    });
  }

  /**
   * Get localized messages for a client's terms or deny message key across all locales.
   */
  getLocalizationMessages(
    clientId: string,
    messageType: 'terms' | 'deny.message'
  ): Promise<{ messages: Record<string, string>; key: string }> {
    const params = new HttpParams().set('clientId', clientId).set('messageType', messageType);

    return lastValueFrom(
      this.http
        .get<{
          success: boolean;
          message: string;
          messages: Record<string, string>;
          key: string;
        }>(`${environment.api.lambdaBaseUrl}/localization/messages`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          retry({
            count: 2,
            delay: (error, retryCount) => {
              const status = error?.status;
              if (status === 502 || status === 503 || status === 504) {
                return timer(retryCount * 1000);
              }
              throw error;
            },
          }),
          map((res) => ({ messages: res.messages, key: res.key })),
          catchError((error) =>
            throwError(() => `Error fetching localization messages: ${error.message}`)
          )
        )
    );
  }

  /**
   * Save/update a single locale's localization text for a client's terms or deny message.
   */
  saveLocalizationMessage(
    clientId: string,
    messageType: 'terms' | 'deny.message',
    locale: string,
    text: string
  ): Promise<void> {
    return lastValueFrom(
      this.http
        .put<{
          success: boolean;
          message: string;
        }>(
          `${environment.api.lambdaBaseUrl}/localization/messages`,
          { clientId, messageType, locale, text },
          { headers: this.getHeaders() }
        )
        .pipe(
          catchError((error) =>
            throwError(() => `Error saving localization message: ${error.message}`)
          )
        )
    ).then(() => void 0);
  }

  /**
   * Delete localization message(s) for a client's terms or deny message.
   * If locale is provided, deletes only that locale's entry.
   * If locale is omitted, deletes ALL locale entries and clears the client attribute.
   */
  deleteLocalizationMessage(
    clientId: string,
    messageType: 'terms' | 'deny.message',
    locale?: string
  ): Promise<void> {
    let params = new HttpParams().set('clientId', clientId).set('messageType', messageType);

    if (locale) {
      params = params.set('locale', locale);
    }

    return lastValueFrom(
      this.http
        .delete<{
          success: boolean;
          message: string;
        }>(`${environment.api.lambdaBaseUrl}/localization/messages`, {
          headers: this.getHeaders(),
          params,
        })
        .pipe(
          catchError((error) =>
            throwError(() => `Error deleting localization message: ${error.message}`)
          )
        )
    ).then(() => void 0);
  }
}
